// 004 (data-model §10): Profile, children and address copy, and the refusal → message mapping
// (FR-022, FR-025, FR-044, FR-052–FR-054). Join refusals reuse 003's JOIN_COPY.
import type { AppError } from "@/api/errors";
import { JOIN_COPY } from "@/join/copy";
import { errorMessage } from "@/shell/StateView";

export const PROFILE_COPY = {
  title: "Profile",
  details: "Your details",
  editDetails: "Edit details",
  email: "Email",
  emailNote: "Your email can't be changed here.",
  name: "Name",
  mobile: "Mobile number (optional)",
  noMobile: "No mobile number",
  save: "Save",
  cancel: "Cancel",
  children: "My children",
  childrenSubtitle:
    "Each child is linked to their school with the school's code.",
  addChild: "Add a child",
  childrenEmpty:
    "Add a child with their school's code. You'll find it on the QR poster at school or in your school's message.",
  childLine: (classLabel: string, section: string, school: string) =>
    `${classLabel} · Section ${section} · ${school}`,
  notLinked: "Not linked to a school",
  linkWithCode: "Link with school code",
  editChild: (name: string) => `Edit ${name}`,
  /** The app bar while the child loads. */
  childTitle: "Edit child",
  removeChild: (name: string) => `Remove ${name}`,
  removeConfirm: (name: string) =>
    `Remove ${name}? Their past orders stay in your order history.`,
  remove: "Remove",
  childRemoved: (name: string) => `${name} has been removed`,
  childSaved: (name: string) => `${name}'s details have been saved`,
  addresses: "Delivery addresses",
  addressesSubtitle: "Orders are delivered to the pin on the map.",
  addAddress: "Add address",
  addressesEmpty:
    "Add an address with a map pin. You need one before you can place an order.",
  defaultBadge: "Default",
  setDefault: "Set as default",
  editAddress: (title: string) => `Edit ${title} address`,
  deleteAddress: (title: string) => `Delete ${title} address`,
  deleteConfirm: "Delete this address?",
  delete: "Delete",
  settings: "Settings",
  logOut: "Log out",
  offlineChange: "You're offline. Connect to make changes.",
  childLimit: "You can add up to 10 children.",
  addressLimit: "You can add up to 10 addresses. Delete one to add another.",
  noClasses: (school: string) =>
    `${school} hasn't set up its classes yet. Please check with your school.`,
  openOrderMove: (child: string) =>
    `${child} has an order that hasn't been delivered yet, so their school can't be changed. You can change it once the order is delivered or cancelled.`,
  openOrderRemove: (child: string) =>
    `${child} has an order that hasn't been delivered yet, so they can't be removed.`,
  notLinkedAny: (child: string) =>
    `${child} isn't linked to a school any more. Link them with a school code.`,
  sameSchool: (child: string, school: string) =>
    `${child} is already at ${school}.`,
  editClassSection: "Edit class and section",
  duplicate: (name: string, school: string) =>
    `${name} is already linked to ${school}.`,
  classGone: "This class is no longer offered. Choose the class again.",
  added: (name: string, school: string) =>
    `${name} has been added to ${school}`,
  addTitle: "Add student & delivery address",
  addSubtitle: "Add your child's details and where orders should be delivered.",
  schoolSection: "This student will be added to",
  schoolFrom: (source: "LINK" | "QR" | "CODE") =>
    source === "QR"
      ? "From the school QR code you scanned"
      : source === "CODE"
        ? "From the school code you entered"
        : "From the school link you opened",
  addNote:
    "Check this is the right school. To add the student to a different school, use Change school and enter that school's code.",
  changeSchoolHeading: "Enter the other school's code",
  keepSchool: (school: string) => `Keep ${school}`,
  saveStudent: "Save student",
  saveStudentAndAddress: "Save student & address",
  stopAdding: (school: string) => `Stop adding a student for ${school}?`,
  stop: "Stop",
  keepGoing: "Keep going",
  studentSection: "Student",
  addressSection: "Delivery address",
  chooseAddress: "Deliver to",
  newAddress: "Add a new address",
  partialFailure:
    "The student was saved, but the address wasn't. Check the address and try again.",
  notFoundChild: "We couldn't find this child.",
  notFoundAddress: "We couldn't find this address.",
  backToProfile: "Back to Profile",
  classLabel: "Class",
  sectionLabel: "Section",
  chooseClass: "Choose a class",
  chooseSection: "Choose a section",
  chooseClassFirst: "Choose a class first",
  changeSchool: "Change school",
  newAddressTitle: "Add address",
  editAddressTitle: "Edit address",
  addressSaved: "Address saved",
} as const;

export type ProfileErrorContext = {
  child?: string;
  school?: string;
  action: "add" | "edit" | "move" | "remove" | "link" | "address";
};

export type ProfileErrorMessage = {
  form?: string;
  fields?: Record<string, string>;
};

const FIELD_ALIASES: Record<string, string> = {
  latitude: "pin",
  longitude: "pin",
};

export function profileErrorMessage(
  error: AppError,
  ctx: ProfileErrorContext,
): ProfileErrorMessage {
  const child = ctx.child ?? "This child";
  const school = ctx.school ?? "this school";
  switch (error.code) {
    case "STUDENT_HAS_OPEN_ORDER":
      return {
        form:
          ctx.action === "remove"
            ? PROFILE_COPY.openOrderRemove(child)
            : PROFILE_COPY.openOrderMove(child),
      };
    case "STUDENT_NOT_LINKED":
      return { form: PROFILE_COPY.notLinkedAny(child) };
    case "DUPLICATE_STUDENT":
      return { fields: { name: PROFILE_COPY.duplicate(child, school) } };
    case "STUDENT_LIMIT_REACHED":
      return { form: PROFILE_COPY.childLimit };
    case "ADDRESS_LIMIT_REACHED":
      return { form: PROFILE_COPY.addressLimit };
    case "SCHOOL_CODE_INVALID":
      return { form: JOIN_COPY.invalid };
    case "SCHOOL_NOT_ACTIVE":
      return { form: JOIN_COPY.notActive(school) };
    case "RATE_LIMITED":
      return { form: JOIN_COPY.rateLimited };
  }
  if (error.fieldErrors.length > 0) {
    const fields: Record<string, string> = {};
    for (const f of error.fieldErrors) {
      const key = FIELD_ALIASES[f.field] ?? f.field;
      fields[key] ??=
        f.code === "NOT_IN_SCHOOL_CLASSES" ? PROFILE_COPY.classGone : f.message;
    }
    return { fields };
  }
  return { form: errorMessage(error) };
}
